import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPost, updatePost, getPostById, downloadTikTokVideo } from './postSlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import ReactDropdown from '../../components/ReactDropdown';
import TikTokSearch from '../../components/TikTokSearch';
import { paths } from '../../routes/helpers';
import api from '../../api/requests';
import { getVideoUrl, getCoverImageUrl, getVideoMetadata } from '../../utils/urlUtils';
import { formatPopulatedLocationLabel } from '../../utils/geoUtils';
import { locationOptionsFromBusinesses } from '../../utils/locationOptionsFromBusinesses';

const PostCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { postId } = useParams();
  const { loading = false, error = null, items = [] } = useSelector((state) => state.post || {});
  const post = postId ? items.find(p => p._id === postId) : null;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for dropdown options
  const [businessOptions, setBusinessOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [trendOptions, setTrendOptions] = useState([]);

  // State for selected values
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTrends, setSelectedTrends] = useState([]);

  // Pagination state
  const [businessPage, setBusinessPage] = useState(1);
  const [locationPage, setLocationPage] = useState(1);
  const [categoryPage, setCategoryPage] = useState(1);
  const [trendPage, setTrendPage] = useState(1);

  const [businessPages, setBusinessPages] = useState(1);
  const [locationPages, setLocationPages] = useState(1);
  const [categoryPages, setCategoryPages] = useState(1);
  const [trendPages, setTrendPages] = useState(1);

  useEffect(() => {
    if (postId) {
      dispatch(getPostById(postId))
        .unwrap()
        .catch(error => {
          toast.error('Failed to load post data');
          log.error('Error fetching post:', error);
          navigate(paths.private.post.list());
        });
    }
  }, [postId, dispatch, navigate]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const limit = 10;
        const [
          businessesResponse,
          categoriesResponse,
          trendsResponse
        ] = await Promise.all([
          api.businesses.getAll({ page: businessPage, limit }),
          api.categories.getAll({ page: categoryPage, limit }),
          api.trends.getAll({ page: trendPage, limit })
        ]);

        // Process businesses
        const businesses = businessesResponse.data.data.businesses.map(business => ({
          value: business._id,
          label: business.title
        }));
        setBusinessOptions(prev => businessPage === 1 ? businesses : [...prev, ...businesses]);
        setBusinessPages(Math.ceil(businessesResponse.data.data.total / limit));

        // If editing and first load, set selected business
        if (postId && businessPage === 1 && post?.businessId) {
          setSelectedBusiness({
            value: post.businessId._id,
            label: post.businessId.title
          });
        }

        const businessList = businessesResponse.data.data.businesses || [];
        const locations = locationOptionsFromBusinesses(businessList);
        setLocationOptions((prev) => (locationPage === 1 ? locations : [...prev, ...locations]));
        setLocationPages(Math.ceil(businessesResponse.data.data.total / limit));

        if (postId && locationPage === 1 && post?.locations) {
          setSelectedLocations(post.locations.map((loc) => ({
            value: loc._id,
            label: formatPopulatedLocationLabel(loc) || loc._id,
          })));
        }

        // Process categories
        const categories = categoriesResponse.data.data.categories.map(category => ({
          value: category._id,
          label: category.label
        }));
        setCategoryOptions(prev => categoryPage === 1 ? categories : [...prev, ...categories]);
        setCategoryPages(Math.ceil(categoriesResponse.data.data.total / limit));

        // If editing and first load, set selected categories
        if (postId && categoryPage === 1 && post?.categories) {
          setSelectedCategories(post.categories.map(cat => ({
            value: cat._id,
            label: cat.label
          })));
        }

        // Process trends
        const trends = trendsResponse.data.data.trends.map(trend => ({
          value: trend._id,
          label: trend.label
        }));
        setTrendOptions(prev => trendPage === 1 ? trends : [...prev, ...trends]);
        setTrendPages(Math.ceil(trendsResponse.data.data.total / limit));

        // If editing and first load, set selected trends
        if (postId && trendPage === 1 && post?.trends) {
          setSelectedTrends(post.trends.map(trend => ({
            value: trend._id,
            label: trend.label
          })));
        }

      } catch (error) {
        console.error('Error fetching options:', error);
        toast.error('Failed to load dropdown options');
      }
    };

    fetchOptions();
  }, [businessPage, locationPage, categoryPage, trendPage, postId, post]);

  const handleLoadMore = (type) => {
    switch (type) {
      case 'business':
        if (businessPage < businessPages) setBusinessPage(prev => prev + 1);
        break;
      case 'location':
        if (locationPage < locationPages) setLocationPage(prev => prev + 1);
        break;
      case 'category':
        if (categoryPage < categoryPages) setCategoryPage(prev => prev + 1);
        break;
      case 'trend':
        if (trendPage < trendPages) setTrendPage(prev => prev + 1);
        break;
      default:
        console.warn('Unknown load more type:', type);
        break;
    }
  };

  const getInitialVideoData = () => {
    if (!post) return null;
    
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const videoMetadata = getVideoMetadata(post);
    
    if (!videoMetadata) return null;

    if (post.videoSource === 'tiktok') {
      return {
        activeTab: 'tiktok',
        searchType: 'user',
        searchTerm: videoMetadata.author || '',
        selectedVideos: [{
          ...videoMetadata,
          playUrl: getVideoUrl(post.videoUrl, baseUrl),
          cover: getCoverImageUrl(videoMetadata.cover, baseUrl)
        }],
        videoSource: 'tiktok'
      };
    } else if (post.videoSource === 'upload') {
      return {
        activeTab: 'upload',
        uploadedVideo: {
          id: `upload-${Date.now()}`,
          title: videoMetadata.title || 'Uploaded Video',
          author: videoMetadata.author || '',
          localUrl: getVideoUrl(post.videoUrl, baseUrl),
          cover: getCoverImageUrl(videoMetadata.cover, baseUrl)
        },
        videoSource: 'upload'
      };
    }
    
    return null;
  };

  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      if (!formData.tikTokUrl) {
        toast.error('Please select a video');
        return;
      }

      // Validate that a video is selected
      if (formData.tikTokUrl.videoSource === 'tiktok' && 
          (!formData.tikTokUrl.selectedVideos || formData.tikTokUrl.selectedVideos.length === 0)) {
        toast.error('Please select a TikTok video');
        return;
      }
      
      if (formData.tikTokUrl.videoSource === 'upload' && 
          !formData.tikTokUrl.uploadedVideo) {
        toast.error('Please upload a video');
        return;
      }

      // Create base post data
      let postData = {
        videoSource: formData.tikTokUrl.videoSource,
        minPrice: formData.minPrice,
        maxPrice: formData.maxPrice,
        status: formData.status,
        businessId: selectedBusiness?.value,
        locations: selectedLocations.map(loc => loc.value),
        categories: selectedCategories.map(cat => cat.value),
        trends: selectedTrends.map(trend => trend.value),
      };

      // Handle video data based on source
      if (formData.tikTokUrl.videoSource === 'tiktok' && formData.tikTokUrl.selectedVideos?.[0]) {
        const tiktokVideo = formData.tikTokUrl.selectedVideos[0];
        
        // If it's a new TikTok video selection
        if (tiktokVideo.playUrl && (!post || post.videoUrl !== tiktokVideo.playUrl)) {
          try {
            const downloadData = await dispatch(downloadTikTokVideo({
              videoUrl: tiktokVideo.playUrl,
              videoMetadata: {
                title: tiktokVideo.title || '',
                author: tiktokVideo.author || '',
                cover: tiktokVideo.cover || '',
                duration: tiktokVideo.duration || '',
                views: tiktokVideo.views || 0,
                description: tiktokVideo.description || '',
                playUrl: tiktokVideo.playUrl || '',
                source: 'tiktok'
              }
            })).unwrap();
            
            postData.videoUrl = downloadData.videoUrl;
            postData.videoMetadata = JSON.stringify(downloadData.videoMetadata);
            
            if (downloadData.videoMetadata?.cover) {
              try {
                const coverResponse = await fetch(downloadData.videoMetadata.cover);
                const coverBlob = await coverResponse.blob();
                const coverFile = new File([coverBlob], 'cover.jpg', { type: 'image/jpeg' });
                postData.coverImage = coverFile;
              } catch (coverError) {
                log.error('Error processing cover image:', coverError);
                toast.error('Failed to process cover image');
              }
            }
          } catch (downloadError) {
            log.error('Error downloading TikTok video:', downloadError);
            toast.error('Failed to download TikTok video. Please try again.');
            return;
          }
        } else {
          // If it's the same video, keep existing data
          postData.videoUrl = post.videoUrl;
          postData.videoMetadata = post.videoMetadata;
        }
      } else if (formData.tikTokUrl.videoSource === 'upload') {
        const videoFile = formData.tikTokUrl.uploadedVideo?.file;
        
        if (videoFile) {
          // New video upload
          const uploadData = new FormData();
          uploadData.append('video', videoFile);
          
          if (formData.tikTokUrl.coverImage) {
            uploadData.append('coverImage', formData.tikTokUrl.coverImage);
          }
          
          const videoMetadata = {
            title: formData.tikTokUrl.uploadedVideo?.title || videoFile.name,
            author: formData.tikTokUrl.uploadedVideo?.author || '',
            size: videoFile.size,
            mimetype: videoFile.type,
            filename: videoFile.name,
            uploadedAt: new Date(),
            source: 'upload'
          };
          
          Object.entries(postData).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach(item => uploadData.append(key, item));
            } else if (typeof value === 'object' && value !== null) {
              uploadData.append(key, JSON.stringify(value));
            } else {
              uploadData.append(key, value);
            }
          });
          
          uploadData.append('videoMetadata', JSON.stringify(videoMetadata));
          postData = uploadData;
        } else if (post) {
          // Keep existing video data
          postData.videoUrl = post.videoUrl;
          postData.videoMetadata = post.videoMetadata;
        }
      }

      if (postId) {
        await dispatch(updatePost({ id: postId, formData: postData })).unwrap();
        toast.success('Post updated successfully!');
      } else {
        await dispatch(createPost(postData)).unwrap();
        toast.success('Post created successfully!');
      }
      navigate(paths.private.post.list());
    } catch (err) {
      toast.error(err.message || 'An error occurred');
      log.error('Error saving post:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = [
    {
      name: 'tikTokUrl',
      label: 'Video*',
      type: 'tiktok-search',
      required: true,
      component: <TikTokSearch />
    },
    {
      name: 'business',
      label: 'Business',
      type: 'dropdown',
      required: true,
      component: (
        <ReactDropdown
          options={businessOptions}
          value={selectedBusiness}
          onChange={setSelectedBusiness}
          isMulti={false}
          required={true}
          onLoadMore={() => handleLoadMore('business')}
          hasMore={businessPage < businessPages}
          isLoading={loading}
        />
      )
    },
    {
      name: 'locations',
      label: 'Locations',
      type: 'dropdown',
      component: (
        <ReactDropdown
          options={locationOptions}
          value={selectedLocations}
          onChange={setSelectedLocations}
          isMulti={true}
          onLoadMore={() => handleLoadMore('location')}
          hasMore={locationPage < locationPages}
          isLoading={loading}
        />
      )
    },
    {
      name: 'categories',
      label: 'Categories',
      type: 'dropdown',
      component: (
        <ReactDropdown
          options={categoryOptions}
          value={selectedCategories}
          onChange={setSelectedCategories}
          isMulti={true}
          onLoadMore={() => handleLoadMore('category')}
          hasMore={categoryPage < categoryPages}
          isLoading={loading}
        />
      )
    },
    {
      name: 'trends',
      label: 'Trends',
      type: 'dropdown',
      component: (
        <ReactDropdown
          options={trendOptions}
          value={selectedTrends}
          onChange={setSelectedTrends}
          isMulti={true}
          onLoadMore={() => handleLoadMore('trend')}
          hasMore={trendPage < trendPages}
          isLoading={loading}
        />
      )
    },
    {
      name: 'minPrice',
      label: 'Minimum Price',
      type: 'number',
      required: true,
      min: 0,
      step: 0.01
    },
    {
      name: 'maxPrice',
      label: 'Maximum Price',
      type: 'number',
      required: true,
      min: 0,
      step: 0.01
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: [
        { value: 0, label: 'Draft' },
        { value: 1, label: 'Published' },
        { value: 2, label: 'Archived' }
      ]
    }
  ];

  const defaultValues = post ? {
    tikTokUrl: getInitialVideoData() || {
      activeTab: 'tiktok',
      searchType: 'user',
      searchTerm: '',
      selectedUser: null,
      selectedVideos: []
    },
    minPrice: post.minPrice || 0,
    maxPrice: post.maxPrice || 0,
    status: post.status || 0
  } : {
    tikTokUrl: {
      activeTab: 'tiktok',
      searchType: 'user',
      searchTerm: '',
      selectedUser: null,
      selectedVideos: []
    },
    minPrice: 0,
    maxPrice: 0,
    status: 0
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {postId ? 'Edit Post' : 'Create Post'}
      </h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading || isSubmitting}
        error={error}
        defaultValues={defaultValues}
        submitText={postId ? 'Update Post' : 'Create Post'}
      />
    </div>
  );
};

export default PostCreateEdit;